import React, {useEffect, useRef, useState} from 'react';
import {Box, Link, Grid, Card, CardContent, Button, IconButton, Typography} from '@mui/material';
import { useAuth } from 'react-oidc-context';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GREEN_LOGO, GREEN_MAIN, WHITE } from '../../theme/colors';
import { dashboard, getPdfCredits } from '../../hooks/actions/dashboard';
import { LastTransaction } from '../../models/dashboard/Transactions';
import { formatDate } from '../../helpers/FormatDate';
import RefreshIcon from '@mui/icons-material/RefreshOutlined'; 
import { useWaiter } from '../../hooks/waiter/WaiterProvider';
import { delay } from '../../utilities/delay';
import { useUserContext } from '../../hooks/auth/UserProvider';
import { checkUserEnabled, isNationalEntityUser } from '../../helpers/authorizedUser';
import settings from '../../utilities/settings';
import CopyToClipboard from '../../components/CopyToClipboard';
import { checkUserAuth } from '../../hooks/actions/users';
import { useFidelityLevel } from '../../hooks/auth/FidelityLevel';
import FidelityLevelSelector from '../../components/FidelityLevelSelector';

const HomeAuth = () => {
    const navigate = useNavigate();
    const {t} = useTranslation();
    const initLoad = useRef(false);

    const [activities, setActivities] = React.useState<LastTransaction[]>([]);
    const [amount, setAmount] = React.useState<number>(0);
    const [pendingAmount, setPendingAmount] = React.useState<number>(0);
    const [points, setPoints] = React.useState<number>(0);
    const [referral, setReferral] = React.useState<string | null>(null);
    const {setWaiterView} = useWaiter();
    const {checkIsAuth, isForbidden, isForbiddenMessage} = useUserContext();
    const [isAssociation, setIsAssociation] = useState<boolean>(false);
    const [isCompany, setIsCompany] = useState<boolean>(false);
    const [isPrivateUser, setIsPrivateUser] = useState<boolean>(false);

    // utente autorizzato
    const {user, auth} = useUserContext();
    const showBuyAndRedeem = isNationalEntityUser(user);
    const [isEnabled, setIsEnabled] = React.useState(false);
    const {fidelityLevel, fetchFidelityLevel} = useFidelityLevel();
    useEffect(()=>{
        if (!auth.isLoading) {
            if (!auth.isAuthenticated) {
                navigate('/');
            } else {
                if(user){
                    
                    // PRIVATE USER
                    if(user.userType === "Private"){
                        setIsPrivateUser(true);
                    }else{
                        setIsPrivateUser(false)
                    }

                    // ENABLED USER
                    if(checkUserEnabled(user)){
                        const url = new URL(settings.ReferralBaseUrl);
                        const cleanedPathname = url.pathname.endsWith('/') ? url.pathname.slice(0, -1) : url.pathname;
                        url.pathname = `${cleanedPathname}/${user.refCode}`;
                        setIsEnabled(true);
                        setReferral(url.toString());
                    }

                    // Association TYPE
                    const associationRoles = ["Association"];
                    const companyRoles = ["Company"];

                    const hasRole = (userRoles: string | string[] | undefined | null, rolesToCheck: string[]): boolean => {
                        if (Array.isArray(userRoles)) {
                            return userRoles.some((role: string) => rolesToCheck.includes(role));
                        } else if (typeof userRoles === "string") {
                            return rolesToCheck.includes(userRoles);
                        }
                        return false; // Se userRoles è undefined o null
                    };
                    if (hasRole(user.role, associationRoles)) {
                        setIsAssociation(true);
                    } else { setIsAssociation(false); }

                    if (hasRole(user.role, companyRoles)) {
                        setIsCompany(true);
                    } else { setIsCompany(false); }

                    // fidelity
                    fetchFidelityLevel().finally();
                }else { 
                    setIsAssociation(false);
                    setIsPrivateUser(false);
                }
            }
        }
        checkIsAuth().finally();
    },[auth.isAuthenticated, auth.isLoading, navigate, user])
    // fine utente autorizzato

    useEffect(()=>{
        if(!initLoad.current){
            initLoad.current = true
            dashboard()
                .then(resp => {
                    setActivities(resp.lastTransaction);
                    setPendingAmount(resp.pendingBalance);
                    setAmount(resp.balance)
                    setPoints(resp.credits)
                })
                .catch(error => {
                    initLoad.current = false
                    console.log(error)
                });
            
            checkIsAuth().finally();

            // fidelity level on startup
            fetchFidelityLevel().finally();
        }
    },[])

    const handleRefresh = () => {
        setWaiterView(true)
        delay(500).finally(()=>{
            dashboard()
                .then(resp => {
                    setActivities(resp.lastTransaction);
                    setAmount(resp.balance)
                    setPendingAmount(resp.pendingBalance);
                    setPoints(resp.credits)
                })
                .catch(error => {
                    initLoad.current = false
                    console.log(error)
                })
            setWaiterView(false)
            checkIsAuth().finally();

            // fidelity level on refresh
            fetchFidelityLevel().finally();
        })
    };

    return(
        <>
            <Box sx={{
                width:'100%', 
                display:'flex',
                flexDirection: 'column',
                color: GREEN_MAIN,
                fontFamily:'poppins'
            }}>
                {
                    auth.isAuthenticated && <>
                        {/* Main title */}
                        <Typography
                            variant="h3"
                            component="h1"
                            gutterBottom
                            sx={{
                                mb: 10,
                                mt:0,
                                fontWeight: "bold",
                            }}
                        >
                            {t("Home")}
                        </Typography>

                        <Grid container spacing={3} paddingBottom={8}>
                            {/* Prima colonna card */}
                            <Grid item xs={12} md={6}>
                                <Grid container spacing={2}>
                                    {/* Card Saldo Asso */}
                                    <Grid item xs={12}>
                                        <Card sx={{ position: 'relative' }}>
                                            <CardContent sx={{
                                                fontWeight: 'bold',
                                                fontSize: '1.2rem',
                                                color: GREEN_MAIN
                                            }}>
                                                {t('Saldo ASSO sul conto')}
                                                {/* Pulsante di aggiornamento */}
                                                <IconButton
                                                    onClick={handleRefresh}
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 8,
                                                        right: 8,
                                                        backgroundColor: 'transparent',
                                                        color:GREEN_MAIN,
                                                        '&:hover': {
                                                            backgroundColor: 'lightblue', // Colore azzurro per hover
                                                        },
                                                    }}
                                                >
                                                    <RefreshIcon />
                                                </IconButton>
                                            </CardContent>
                                            <CardContent sx={{
                                                fontFamily:'Poppins',
                                                fontSize: 40,
                                                fontWeight: 700,
                                                lineHeight: 1.2,
                                                // letterSpacing: '0.2rem',
                                                color: GREEN_MAIN,
                                            }}>
                                                {amount.toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                            </CardContent>
                                            {pendingAmount > 0 && 
                                            <>
                                                <CardContent sx={{
                                                    fontSize: '1.2rem',
                                                    letterSpacing: '0.1rem',
                                                    color: GREEN_MAIN
                                                }}>
                                                    {t("Importo bloccato: ")} {pendingAmount.toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                                </CardContent>
                                            </>}
                                            <CardContent>
                                                <Grid container spacing={2} justifyContent="center">
                                                    { !isForbidden && showBuyAndRedeem &&
                                                        <Grid item md={!isPrivateUser ? 3 : 5} sm={6} xs={12}>
                                                            <Button variant="contained"
                                                                onClick={()=>{navigate('/buy')}}
                                                                disabled={!isEnabled}
                                                                sx={{
                                                                    // margin:'10px',
                                                                    backgroundColor: `${GREEN_LOGO}`,
                                                                    color: WHITE,
                                                                    // maxWidth: '200px',
                                                                    textTransform: 'none',
                                                                    letterSpacing: '0.02rem',
                                                                    fontSize:'0.8em',
                                                                    '&:hover': {
                                                                        backgroundColor: `${GREEN_MAIN}`, // Colore azzurro per hover
                                                                        color: WHITE,
                                                                        border: `1px solid ${GREEN_LOGO}`
                                                                    },
                                                                    '&.Mui-disabled': {
                                                                        color: WHITE, 
                                                                        cursor: 'not-allowed', 
                                                                    },
                                                                }}>
                                                                {t("Acquista")}
                                                            </Button>
                                                        </Grid>
                                                    }
                                                    <Grid item md={!isPrivateUser ? 3 : 5} sm={6} xs={12}>
                                                        <Button variant="contained"
                                                            onClick={()=>{navigate('/withdraw')}}
                                                            disabled={false}
                                                            sx={{
                                                                // margin:'10px',
                                                                backgroundColor: `${GREEN_LOGO}`,
                                                                color: WHITE,
                                                                // maxWidth: '200px',
                                                                textTransform: 'none',
                                                                letterSpacing: '0.02rem',
                                                                fontSize:'0.8em',
                                                                '&:hover': {
                                                                    backgroundColor: `${GREEN_MAIN}`, // Colore azzurro per hover
                                                                    color: WHITE,
                                                                    border: `1px solid ${GREEN_LOGO}`
                                                                },
                                                                '&.Mui-disabled': {
                                                                    color: WHITE, 
                                                                    cursor: 'not-allowed', 
                                                                },
                                                            }}>
                                                            {t("Trasferisci")}
                                                        </Button>
                                                    </Grid>
                                                    {  !isPrivateUser && showBuyAndRedeem ?
                                                        (<>
                                                            { /**!isForbidden && 
                                                                <Grid item md={3} sm={6} xs={12}>
                                                                    <Button variant="contained"
                                                                        onClick={()=>{navigate('/deposit')}}
                                                                        disabled={!isEnabled || isPrivateUser}
                                                                        sx={{
                                                                            // margin:'10px',
                                                                            backgroundColor: `${GREEN_LOGO}`,
                                                                            color: WHITE,
                                                                            // maxWidth: '200px',
                                                                            textTransform: 'none',
                                                                            letterSpacing: '0.02rem',
                                                                            fontSize:'0.8em',
                                                                            '&:hover': {
                                                                                backgroundColor: `${GREEN_MAIN}`, // Colore azzurro per hover
                                                                                color: WHITE,
                                                                                border: `1px solid ${GREEN_LOGO}`
                                                                            },
                                                                            '&.Mui-disabled': {
                                                                                color: WHITE, 
                                                                                cursor: 'not-allowed', 
                                                                            },
                                                                        }}>
                                                                        {t("Deposita")}
                                                                    </Button>
                                                                </Grid>
                                                            */}
                                                            <Grid item md={3} sm={6} xs={12}>   
                                                                <Button variant="contained"
                                                                    onClick={()=>{navigate('/redeem')}}
                                                                    disabled={isPrivateUser}
                                                                    sx={{
                                                                        // margin:'10px',
                                                                        backgroundColor: `${GREEN_LOGO}`,
                                                                        color: WHITE,
                                                                        // maxWidth: '200px',
                                                                        textTransform: 'none',
                                                                        letterSpacing: '0.02rem',
                                                                        fontSize:'0.8em',
                                                                        '&:hover': {
                                                                            backgroundColor: `${GREEN_MAIN}`, // Colore azzurro per hover
                                                                            color: WHITE,
                                                                            border: `1px solid ${GREEN_LOGO}`
                                                                        },
                                                                        '&.Mui-disabled': {
                                                                            color: WHITE, 
                                                                            cursor: 'not-allowed', 
                                                                        },
                                                                    }}>
                                                                    {t("Riscatta")}
                                                                </Button>
                                                            </Grid>
                                                        </>) : null  
                                                    }
                                                </Grid>
                                            </CardContent>
                                        </Card>
                                    </Grid>

                                </Grid>
                            </Grid>

                            {/* Seconda colonna card */}
                            <Grid item xs={12} md={6}>
                                <Grid container spacing={2}>
                                    {/* Card Attività Recenti */}
                                    <Grid item xs={12}>
                                        <Card  sx={{ position: 'relative' }}>
                                            <CardContent sx={{
                                                fontWeight:'bold',
                                                fontSize:'1.2rem',
                                                color: GREEN_MAIN
                                            }}>
                                                {t("Attività Recenti")}
                                                {/* Pulsante di aggiornamento */}
                                                <IconButton
                                                    onClick={handleRefresh}
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 8,
                                                        right: 8,
                                                        backgroundColor: 'transparent',
                                                        color:GREEN_MAIN,
                                                        '&:hover': {
                                                            backgroundColor: 'lightblue', // Colore azzurro per hover
                                                        },
                                                    }}
                                                >
                                                    <RefreshIcon />
                                                </IconButton>
                                            </CardContent>
                                            <CardContent sx={{
                                            }}>
                                                {activities.length > 0 ? 
                                                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign:'left', color:GREEN_MAIN }}>
                                                    <tbody>
                                                    {activities.slice(0, 5).map((activity, index) => (
                                                        <tr key={index}>
                                                            <td style={{ paddingRight: '1rem', width:'45%' }}>
                                                                {formatDate(activity.data)}
                                                            </td>
                                                            <td style={{ width:'55%' }}>
                                                                {t(activity.description)}
                                                            </td>
                                                            <td style={{ width:'55%', textAlign:'right' }}>
                                                                {activity.amount.toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                    </tbody>
                                                </table>
                                                :
                                                t("Non hai ancora transazioni")
                                                }
                                            </CardContent>
                                            {activities.length > 0 ?
                                            <CardContent>
                                                {/* <Link href="/transactions" sx={{ color: WHITE, textDecoration: 'none' }}>
                                                    {t("Mostra tutto")}
                                                </Link> */}
                                                <Grid container spacing={2} justifyContent="space-around">
                                                    <Grid item md={5} sm={6} xs={12}>
                                                        <Button variant="contained"
                                                            onClick={()=>{navigate('/transactions')}}
                                                            disabled={!isEnabled}
                                                            sx={{
                                                                // margin:'10px',
                                                                backgroundColor: `${GREEN_LOGO}`,
                                                                color: WHITE,
                                                                // maxWidth: '200px',
                                                                textTransform: 'none',
                                                                letterSpacing: '0.02rem',
                                                                fontSize:'0.8em',
                                                                '&:hover': {
                                                                    backgroundColor: `${GREEN_MAIN}`, // Colore azzurro per hover
                                                                    color: WHITE,
                                                                    border: `1px solid ${GREEN_LOGO}`
                                                                },
                                                                '&.Mui-disabled': {
                                                                    color: WHITE, 
                                                                    cursor: 'not-allowed', 
                                                                },
                                                            }}>
                                                            {t("Mostra tutto")}
                                                        </Button>
                                                    </Grid>
                                                </Grid>
                                            </CardContent> : null}
                                        </Card>
                                    </Grid>
                                    {/* Card Punti Fedeltà */}
                                    {/* <Grid item xs={12}>
                                        <Card sx={{ position: 'relative' }}>
                                            <CardContent sx={{
                                                fontWeight: 'bold',
                                                fontSize: '1.2rem',
                                                color: GREEN_MAIN
                                            }}>
                                                {t('Punti Fedeltà')}
                                                // Pulsante di aggiornamento
                                                <IconButton
                                                    onClick={handleRefresh}
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 8,
                                                        right: 8,
                                                        backgroundColor: 'transparent',
                                                        color:GREEN_MAIN,
                                                        '&:hover': {
                                                            backgroundColor: 'lightblue', // Colore azzurro per hover
                                                        },
                                                    }}
                                                >
                                                    <RefreshIcon />
                                                </IconButton>
                                            </CardContent>
                                            <CardContent sx={{
                                                fontFamily:'Poppins',
                                                fontSize: 40,
                                                fontWeight: 700,
                                                lineHeight: 1.2,
                                                // letterSpacing: '0.2rem',
                                                color: GREEN_MAIN,
                                                paddingBottom:'5px'
                                            }}>
                                                {points}
                                            </CardContent>
                                            <Typography sx={{
                                                fontFamily:'Poppins',
                                                fontSize: 20,
                                                fontWeight: 'bold',
                                                lineHeight: 1.2,
                                                // letterSpacing: '0.2rem',
                                                color: GREEN_MAIN,
                                                paddingBottom:'16px'
                                            }}>
                                                {fidelityLevel.level} LEVEL!
                                            </Typography>
                                            <CardContent>
                                                <Grid container spacing={2} justifyContent="center">
                                                { !isForbidden && 
                                                        <Grid item md={5} sm={6} xs={12}>
                                                            <Button variant="contained"
                                                                onClick={()=>{navigate('/fidelity')}}
                                                                disabled={!isEnabled}
                                                                sx={{
                                                                    // margin:'10px',
                                                                    backgroundColor: `${GREEN_LOGO}`,
                                                                    color: WHITE,
                                                                    // maxWidth: '200px',
                                                                    textTransform: 'none',
                                                                    letterSpacing: '0.02rem',
                                                                    fontSize:'0.8em',
                                                                    '&:hover': {
                                                                        backgroundColor: `${GREEN_MAIN}`, // Colore azzurro per hover
                                                                        color: WHITE,
                                                                        border: `1px solid ${GREEN_LOGO}`
                                                                    },
                                                                    '&.Mui-disabled': {
                                                                        color: WHITE, 
                                                                        cursor: 'not-allowed', 
                                                                    },
                                                                }}>
                                                                {t("Guadagna punti")}
                                                            </Button>
                                                        </Grid>
                                                    }
                                                    <Grid item md={5} sm={6} xs={12}>
                                                        <Button variant="contained"
                                                            onClick={() => {getPdfCredits().finally()}}
                                                            disabled={false}
                                                            sx={{
                                                                // margin:'10px',
                                                                backgroundColor: `${GREEN_LOGO}`,
                                                                color: WHITE,
                                                                // maxWidth: '200px',
                                                                textTransform: 'none',
                                                                letterSpacing: '0.02rem',
                                                                fontSize:'0.8em',
                                                                '&:hover': {
                                                                    backgroundColor: `${GREEN_MAIN}`, // Colore azzurro per hover
                                                                    color: WHITE,
                                                                    border: `1px solid ${GREEN_LOGO}`
                                                                },
                                                                '&.Mui-disabled': {
                                                                    color: WHITE, 
                                                                    cursor: 'not-allowed', 
                                                                },
                                                            }}>
                                                            {t("Esporta")}
                                                        </Button>
                                                    </Grid>
                                                </Grid>
                                            </CardContent>
                                        </Card>
                                    </Grid> */}

                                    

                                    {/* Card Referral Link */}
                                    {/* {!isAssociation && <Grid item xs={12}>
                                        <Card sx={{ position: 'relative' }}>
                                            <CardContent sx={{
                                                fontWeight:'bold',
                                                fontSize:'1.3rem',
                                            }}>
                                                {t('Il Tuo Referral')}
                                                
                                            </CardContent>
                                            <CardContent sx={{
                                                fontSize:'0.65rem',
                                                letterSpacing:'0.1rem',
                                            }}>
                                                {   
                                                    referral &&
                                                    <CopyToClipboard text={referral} />
                                                }
                                            </CardContent>
                                            <CardContent>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12}>
                                                        {t("Clicca sul link per copiare il tuo referral link.")}
                                                    </Grid>
                                                </Grid> 
                                            </CardContent>
                                        </Card>
                                    </Grid>} */}
                                    
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid container spacing={3} paddingBottom={8}>
                            <Grid item xs={12}
                                onClick={()=>{window.location.href = settings.MarketPlace.Url}}
                                sx={{
                                    cursor: 'pointer', // fa comparire la manina al passaggio
                                    '&:hover': {
                                        opacity: 0.8,     // opzionale: effetto visivo al passaggio
                                    },
                                }}
                            >
                                <CardContent sx={{
                                    backgroundColor:undefined,
                                    paddingTop:'25px',
                                    color:GREEN_MAIN,
                                    fontSize:20,
                                    fontWeight:'bold',
                                }}>
                                    {isCompany === false ? t('Compra online') : t('Compra e vendi su')}
                                </CardContent>
                                <CardContent sx={{
                                    backgroundColor:undefined,
                                    paddingTop:'10px'
                                }}>
                                    <img src={`/${settings.MarketPlace.Logo}`} style={{
                                        maxWidth:'250px'
                                    }}/>
                                </CardContent>
                            </Grid>
                        </Grid>
                        
                        {/* <FidelityLevelSelector 
                            gender={fidelityLevel.gender} 
                            level={fidelityLevel.level} 
                        /> */}
                    </>
                }
            </Box>
        </>
    )
}

export default HomeAuth;